/**
 * Roster generation — the persistent simulated society (CLAUDE.md mechanic #1).
 *
 * `makeSociety(seed, size)` deterministically births hundreds of persistent
 * trainers: each with a stable id + username, a Pokemon avatar, a personality, a
 * home zone, a play style, a starting team, neutral friendship with the player,
 * and a few warm inter-trainer relationships. Pure + deterministic: the same
 * (seed, size) always yields byte-identical output, and a larger size keeps the
 * smaller roster as a prefix (so the world can grow without reshuffling).
 *
 * Warm by construction: player-facing affinity starts neutral and is never seeded
 * negative; any drama lives only *between* sim trainers (see tick.ts).
 *
 * Species data is injected so this layer stays pure + hermetic; it defaults to the
 * hand-authored fixtures (tests) and is wired to the generated dataset in prod.
 */
import type {
  SpeciesData,
  PokemonInstance,
  SimTrainer,
  Personality,
  PlayStyle,
  Relationship,
  MoveSlot,
  IVs,
  EVs,
  StatKey,
} from '@/shared';
import { STAT_KEYS, LEVEL_MAX } from '@/shared';
import { makeRng, hashString, type Rng } from '@/core';
import { computeStats, NATURES } from '../core/stats.js';
import { expForLevel } from '../core/leveling.js';
import { FIXTURE_SPECIES } from '@/data';

/** Neutral starting friendship with the player (0..100). */
export const NEUTRAL_FRIENDSHIP = 50;

/** Neutral starting mon friendship/happiness (0..255). */
const NEUTRAL_MON_FRIENDSHIP = 70;

/** Stable wall-clock anchor so deterministic output never depends on Date.now(). */
export const EPOCH = 1_700_000_000_000;

const PLAY_STYLES: readonly PlayStyle[] = [
  'collector',
  'battler',
  'breeder',
  'explorer',
  'socializer',
];

/** Home zones — biome-flavored node ids the roster spreads across. */
const HOME_NODES: readonly string[] = [
  'meadow',
  'forest',
  'cave',
  'mountain',
  'lake',
  'sea',
  'beach',
  'marsh',
  'grassland',
  'ruins',
  'town',
];

/** Guild ids; `null` (unaffiliated) is also possible — belonging is never forced. */
const GUILDS: readonly string[] = [
  'emberguard',
  'tidecallers',
  'leafwardens',
  'stormchasers',
  'moonlit-circle',
];

/** Username parts — combined deterministically into hundreds of distinct handles. */
const NAME_ADJ: readonly string[] = [
  'Sunny',
  'Mossy',
  'Bright',
  'Cozy',
  'Lucky',
  'Quiet',
  'Brave',
  'Merry',
  'Gentle',
  'Swift',
  'Dawn',
  'Dusk',
  'Amber',
  'Coral',
  'Pebble',
  'Cloud',
  'Maple',
  'Fern',
  'Sage',
  'Clover',
];

const NAME_NOUN: readonly string[] = [
  'Brook',
  'Hollow',
  'Fennel',
  'Pippin',
  'Tansy',
  'Birch',
  'Wren',
  'Otter',
  'Sparrow',
  'Comet',
  'Willow',
  'Juniper',
  'Marsh',
  'Cobble',
  'Lark',
  'Thistle',
  'Berry',
  'Acorn',
  'Pebbly',
  'Drift',
];

/** Per-play-style flavor traits the voice layer can lean on. */
const STYLE_TRAITS: Readonly<Record<PlayStyle, readonly string[]>> = {
  collector: ['loves filling the dex', 'photographs every catch', 'keeps meticulous notes'],
  battler: ['lives for a good spar', 'studies type matchups', 'cheers loudly in battle'],
  breeder: ['dotes on eggs', 'happy to explain natures', 'always trading for new bloodlines'],
  explorer: ['always wandering somewhere new', 'collects pressed flowers', 'maps the routes'],
  socializer: ['greets everyone by name', 'organizes little meetups', 'tells warm stories'],
};

const EXTRA_TRAITS: readonly string[] = [
  'always punning',
  'loves bug types',
  'brings snacks',
  'hums while walking',
  'never without a sun hat',
  'collects shiny bottle caps',
  'early riser',
  'night owl',
];

const RELATIONSHIP_KINDS: readonly string[] = ['friend', 'acquaintance', 'mentor', 'rival'];

// ── Deterministic instance construction ──────────────────────────────────────

function rollSpread(rng: Rng, max: number): Record<StatKey, number> {
  const out = {} as Record<StatKey, number>;
  for (const k of STAT_KEYS) out[k] = rng.int(max + 1);
  return out;
}

/**
 * Build one owned mon deterministically from an injected Rng. Levels/stats are
 * faithful (real stat + EXP formulas, reused from core), so a sim trainer's team
 * is indistinguishable in shape from a player's.
 */
export function makeInstance(
  rng: Rng,
  species: SpeciesData,
  level: number,
  owner: { id: string; name: string },
): PokemonInstance {
  const lvl = Math.max(1, Math.min(LEVEL_MAX, Math.trunc(level)));
  const ivs = rollSpread(rng, 31) as IVs;
  const evs = { hp: 0, atk: 0, def: 0, spatk: 0, spdef: 0, spe: 0 } as EVs;
  const nature = rng.pick(NATURES);
  const stats = computeStats(species.baseStats, ivs, evs, lvl, nature);

  // Up to four most-recent level-up moves the species knows by this level.
  const learnable = species.learnset
    .filter((e) => e.method === 'level-up' && (e.level ?? 1) <= lvl)
    .sort((a, b) => (a.level ?? 1) - (b.level ?? 1));
  const chosen = learnable.slice(-4);
  const moveList = chosen.length > 0 ? chosen : species.learnset.slice(0, 1);
  const moves: MoveSlot[] = moveList.map((e) => ({ moveId: e.moveId, pp: 0, maxPp: 0 }));

  const abilityId = species.abilities.primary;
  let gender: 'male' | 'female' | null = null;
  if (species.genderRatio !== null) {
    gender = rng.float() < species.genderRatio ? 'female' : 'male';
  }

  return {
    id: `${owner.id}-mon-${rng.getState().toString(36)}-${species.id}`,
    speciesId: species.id,
    nickname: null,
    level: lvl,
    exp: expForLevel(species.growthRate, lvl),
    natureId: nature.id,
    ivs,
    evs,
    abilityId,
    moves,
    currentHp: stats.hp,
    maxHp: stats.hp,
    status: 'none',
    statusTurns: 0,
    heldItemId: null,
    friendship: NEUTRAL_MON_FRIENDSHIP,
    shiny: false,
    gender,
    originalTrainer: { trainerId: owner.id, trainerName: owner.name, viaTrade: false },
    obtainedAt: EPOCH,
  };
}

// ── One trainer ───────────────────────────────────────────────────────────────

function makePersonality(rng: Rng, style: PlayStyle): Personality {
  const traits = [rng.pick(STYLE_TRAITS[style])];
  if (rng.chance(0.5)) {
    const extra = rng.pick(EXTRA_TRAITS);
    if (!traits.includes(extra)) traits.push(extra);
  }
  return {
    warmth: round2(rng.range(0.45, 1)), // never cold — warm world
    energy: round2(rng.float()),
    humor: round2(rng.float()),
    chattiness: round2(rng.range(0.3, 1)),
    traits,
  };
}

function round2(x: number): number {
  return Math.round(x * 100) / 100;
}

/**
 * Birth trainer #i. Its identity is seeded purely from (worldSeed, i) so the roster
 * is prefix-stable: growing `size` never disturbs earlier trainers.
 */
function makeTrainer(
  worldSeed: number,
  index: number,
  species: readonly SpeciesData[],
): SimTrainer {
  const rng = makeRng(hashString(`society|${worldSeed >>> 0}|trainer|${index}`));
  const id = `t${index}`;

  const adj = NAME_ADJ[rng.int(NAME_ADJ.length)]!;
  const noun = NAME_NOUN[rng.int(NAME_NOUN.length)]!;
  const username = `${adj}${noun}${index}`;

  const avatar = rng.pick(species);
  const playStyle = rng.pick(PLAY_STYLES);
  const homeNodeId = rng.pick(HOME_NODES);
  const personality = makePersonality(rng, playStyle);

  // A minority are full-detail "regulars" (the rotating named cast); the rest are
  // the simulated-in-aggregate crowd (CLAUDE.md level-of-detail).
  const lod: 'regular' | 'crowd' = rng.chance(0.22) ? 'regular' : 'crowd';

  const guildId = rng.chance(0.55) ? rng.pick(GUILDS) : null;

  const teamSize = 1 + rng.int(4); // 1..4
  const team: PokemonInstance[] = [];
  for (let s = 0; s < teamSize; s++) {
    const sp = rng.pick(species);
    const level = 5 + rng.int(26); // 5..30 — gentle starting band
    team.push(makeInstance(rng, sp, level, { id, name: username }));
  }

  return {
    id,
    username,
    avatarSpeciesId: avatar.id,
    personality,
    playStyle,
    homeNodeId,
    team,
    friendshipWithPlayer: NEUTRAL_FRIENDSHIP,
    relationships: [], // wired in a second pass once all ids exist
    memories: [],
    guildId,
    lod,
  };
}

// ── Public entry ───────────────────────────────────────────────────────────────

/**
 * Create the initial society of `size` persistent simulated trainers.
 * Deterministic from `seed`; prefix-stable in `size`.
 */
export function makeSociety(
  seed: number,
  size: number,
  species: readonly SpeciesData[] = FIXTURE_SPECIES,
): readonly SimTrainer[] {
  const n = Math.max(0, Math.trunc(size));
  if (n === 0) return [];
  if (species.length === 0) throw new Error('makeSociety needs at least one species');

  const trainers: SimTrainer[] = [];
  for (let i = 0; i < n; i++) {
    trainers.push(makeTrainer(seed, i, species));
  }

  // Second pass: seed a few warm inter-trainer relationships per trainer. Done
  // after all ids exist so every reference points at a real trainer. Prefix-stable
  // because each trainer's partners are drawn only from *earlier* trainers.
  for (let i = 0; i < trainers.length; i++) {
    const t = trainers[i]!;
    if (i === 0) continue;
    const rel = makeRng(hashString(`society|${seed >>> 0}|rel|${i}`));
    const want = Math.min(i, rel.int(3) + (t.lod === 'regular' ? 2 : 0)); // regulars are more connected
    const used = new Set<number>();
    const relationships: Relationship[] = [];
    for (let r = 0; r < want; r++) {
      const j = rel.int(i); // an earlier trainer
      if (used.has(j)) continue;
      used.add(j);
      const kind = rel.pick(RELATIONSHIP_KINDS);
      // Rivals start slightly cool *with each other* (NPC-only drama); friends warm.
      const affinity = kind === 'rival' ? -10 + rel.int(20) : 20 + rel.int(60);
      relationships.push({ otherId: trainers[j]!.id, affinity, kind });
    }
    t.relationships = relationships;
  }

  return trainers;
}
